import React from 'react';
import clsx from 'clsx';
import { makeStyles, Theme } from '@material-ui/core/styles';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import NamedBox from '../atoms/NamedBox';
import { COLOR_BACKGROUND_WARM, COLOR_BORDER, COLOR_MIDDLE_GRAY, COLOR_TEXT, COLOR_WHITE } from '../styles/colors';
import { FONT_PROXIMA_NOVA } from '../styles/fonts';
import { useTableSort } from '../hooks/useTableSort';
import { NAIAProductionRow } from '../types/CollegeProductionData';


interface NAIAProductionTableProps {
  className?: string;
  data?: NAIAProductionRow[] | null;
}

const useStyles = makeStyles((theme: Theme) => {
  const firstColWidth = 70;
  const secondColWidth = 170;

  return {
    root: {},
    headerName: {
      fontFamily: FONT_PROXIMA_NOVA,
    },
    tableContainer: {
      maxHeight: 360,
      overflow: 'auto',
    },
    table: {
      width: '100%',
      minWidth: 960,
      borderCollapse: 'collapse' as const,
      tableLayout: 'fixed' as const,
      fontFamily: FONT_PROXIMA_NOVA,
      fontSize: theme.typography.pxToRem(12),
    },
    theadCell: {
      borderBottom: `1px solid ${COLOR_BORDER}`,
      borderRight: `1px solid ${COLOR_BORDER}`,
      padding: theme.spacing(1),
      // background: COLOR_BACKGROUND_WARM,
      color: COLOR_TEXT,
      fontWeight: 700,
      textAlign: 'center' as const,
      userSelect: 'none' as const,
      cursor: 'pointer',
      // zIndex: 2,
    },
    theadGroupCell: {
      borderBottom: `1px solid ${COLOR_BORDER}`,
      borderRight: `1px solid ${COLOR_BORDER}`,
      padding: theme.spacing(1),
      background: COLOR_MIDDLE_GRAY,
      color: COLOR_TEXT,
      fontWeight: 700,
      textAlign: 'center' as const,
      // zIndex: 2,
    },
    stickyHeaderTop: {
      position: 'sticky' as const,
      top: 0,
    },
    stickyHeaderSecond: {
      position: 'sticky' as const,
      top: 32,
    },
    thFirstCol: {
      minWidth: firstColWidth,
      maxWidth: firstColWidth,
      left: 0,
      // zIndex: 3,
    },
    thSecondCol: {
      minWidth: secondColWidth,
      maxWidth: secondColWidth,
      left: firstColWidth,
      // zIndex: 3,
    },
    stickyCol: {
      position: 'sticky' as const,
      // background: COLOR_BACKGROUND_WARM,
    },
    bodyRow: {
      background: COLOR_WHITE,
    },
    bodyCell: {
      borderBottom: `1px solid ${COLOR_MIDDLE_GRAY}`,
      borderRight: `1px solid ${COLOR_BORDER}`,
      padding: theme.spacing(0.75, 1),
      textAlign: 'center' as const,
      whiteSpace: 'nowrap' as const,
    },
    bodyCellFirst: {
      position: 'sticky' as const,
      left: 0,
      minWidth: firstColWidth,
      maxWidth: firstColWidth,
      background: COLOR_WHITE,
      // zIndex: 2,
      textAlign: 'left' as const,
    },
    bodyCellSecond: {
      position: 'sticky' as const,
      left: firstColWidth,
      minWidth: secondColWidth,
      maxWidth: secondColWidth,
      background: COLOR_WHITE,
      // zIndex: 2,
      textAlign: 'left' as const,
    },
    yearCell: {
      fontWeight: 700,
    },
    groupHeaderRow: {
      borderBottom: `1px solid ${COLOR_BORDER}`,
    },
    groupHeaderCell: {
      background: COLOR_BACKGROUND_WARM,
      borderRight: `1px solid ${COLOR_BORDER}`,
      padding: theme.spacing(1),
      fontWeight: 700,
      textAlign: 'center' as const,
    },
    sortLabelIcon: {
      opacity: 1,
    },
  };
}, { name: 'NAIAProductionTable' });

export default function NAIAProductionTable(props: NAIAProductionTableProps) {
  const { className, data } = props;
  const classes = useStyles();

  // const MOCK_NAIA_PRODUCTION: NAIAProductionResponse = {
  //   idType: 'tfId',
  //   idValue: 10000,
  //   productionData: [
  //     {
  //       year: 2025,
  //       teamName: '',
  //       gamesPlayed: 0,
  //       passingComp: 0,
  //       passingAtt: 0,
  //       passingYds: 0,
  //       passingTD: 0,
  //       passingInt: 0,
  //       rushingAtt: 0,
  //       rushingYds: 0,
  //       rushingTD: 0,
  //       receiving: 0,
  //       receivingYds: 0,
  //       receivingTD: 0,
  //       tackles: 29,
  //       sacks: 0,
  //       interceptions: 3,
  //     },
  //   ],
  // };

  // const rows: NAIAProductionRow[] = (data ?? MOCK_NAIA_PRODUCTION).productionData;
  // const rows: NAIAProductionRow[] = data || [];
  const rows: NAIAProductionRow[] = data || [];
  const { sortKey, sortDirection, handleSort, sortedRows } = useTableSort<NAIAProductionRow, keyof NAIAProductionRow>({
    rows,
    initialSortKey: 'year',
    initialDirection: 'desc',
    nextKeyDirection: 'desc',
  });

  function renderSortableHeader(
    label: string,
    key: keyof NAIAProductionRow,
    className: string,
    rowSpan?: number,
  ) {
    const isActive = sortKey === key;
    return (
      <th className={className} rowSpan={rowSpan}>
        <TableSortLabel
          active={isActive}
          direction={isActive ? sortDirection : 'asc'}
          hideSortIcon={false}
          IconComponent={(iconProps) => (
            <ArrowDownwardIcon
              {...iconProps}
              style={{
                color: isActive ? '#2f6bff' : '#b5b5b5',
              }}
            />
          )}
          classes={{ icon: classes.sortLabelIcon }}
          onClick={() => handleSort(key)}
        >
          {label}
        </TableSortLabel>
      </th>
    );
  }

  return (
    <NamedBox
      className={clsx(classes.root, className)}
      headerNameClassName={classes.headerName}
      name="NAIA Stats"
    >
      <div className={classes.tableContainer}>
        <table className={classes.table}>
          <thead>
            <tr>
              {renderSortableHeader(
                'Year',
                'year',
                clsx(
                  classes.theadCell,
                  classes.stickyHeaderTop,
                  classes.stickyCol,
                  classes.thFirstCol,
                ),
                2,
              )}
              {renderSortableHeader(
                'Team',
                'teamName',
                clsx(
                  classes.theadCell,
                  classes.stickyHeaderTop,
                  classes.stickyCol,
                  classes.thSecondCol,
                ),
                2,
              )}
              {renderSortableHeader(
                'G',
                'gamesPlayed',
                clsx(classes.theadCell, classes.stickyHeaderTop),
                2,
              )}
              <th
                className={clsx(classes.theadGroupCell, classes.stickyHeaderTop)}
                colSpan={5}
              >
                Passing
              </th>
              <th
                className={clsx(classes.theadGroupCell, classes.stickyHeaderTop)}
                colSpan={3}
              >
                Rushing
              </th>
              <th
                className={clsx(classes.theadGroupCell, classes.stickyHeaderTop)}
                colSpan={3}
              >
                Receiving
              </th>
              <th
                className={clsx(classes.theadGroupCell, classes.stickyHeaderTop)}
                colSpan={3}
              >
                Defense
              </th>
            </tr>
            <tr className={classes.groupHeaderRow}>
              {/* Passing */}
              {renderSortableHeader('Comp', 'passingComp', clsx(classes.theadCell, classes.stickyHeaderSecond))}
              {renderSortableHeader('Att', 'passingAtt', clsx(classes.theadCell, classes.stickyHeaderSecond))}
              {renderSortableHeader('Yds', 'passingYds', clsx(classes.theadCell, classes.stickyHeaderSecond))}
              {renderSortableHeader('TD', 'passingTD', clsx(classes.theadCell, classes.stickyHeaderSecond))}
              {renderSortableHeader('INT', 'passingInt', clsx(classes.theadCell, classes.stickyHeaderSecond))}
              {/* Rushing */}
              {renderSortableHeader('Att', 'rushingAtt', clsx(classes.theadCell, classes.stickyHeaderSecond))}
              {renderSortableHeader('Yds', 'rushingYds', clsx(classes.theadCell, classes.stickyHeaderSecond))}
              {renderSortableHeader('TD', 'rushingTD', clsx(classes.theadCell, classes.stickyHeaderSecond))}
              {/* Receiving */}
              {renderSortableHeader('Rec', 'receiving', clsx(classes.theadCell, classes.stickyHeaderSecond))}
              {renderSortableHeader('Yds', 'receivingYds', clsx(classes.theadCell, classes.stickyHeaderSecond))}
              {renderSortableHeader('TD', 'receivingTD', clsx(classes.theadCell, classes.stickyHeaderSecond))}
              {/* Defense */}
              {renderSortableHeader('Tckls', 'tackles', clsx(classes.theadCell, classes.stickyHeaderSecond))}
              {renderSortableHeader('Sacks', 'sacks', clsx(classes.theadCell, classes.stickyHeaderSecond))}
              {renderSortableHeader('INTs', 'interceptions', clsx(classes.theadCell, classes.stickyHeaderSecond))}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row) => (
              <tr key={`${row.year}-${row.teamName}`} className={classes.bodyRow}>
                <td className={clsx(classes.bodyCell, classes.bodyCellFirst, classes.yearCell)}>
                  {row.year}
                </td>
                <td className={clsx(classes.bodyCell, classes.bodyCellSecond)}>
                  {row.teamName}
                </td>
                <td className={classes.bodyCell}>{row.gamesPlayed}</td>
                {/* Passing */}
                <td className={classes.bodyCell}>{row.passingComp}</td>
                <td className={classes.bodyCell}>{row.passingAtt}</td>
                <td className={classes.bodyCell}>{row.passingYds}</td>
                <td className={classes.bodyCell}>{row.passingTD}</td>
                <td className={classes.bodyCell}>{row.passingInt}</td>
                {/* Rushing */}
                <td className={classes.bodyCell}>{row.rushingAtt}</td>
                <td className={classes.bodyCell}>{row.rushingYds}</td>
                <td className={classes.bodyCell}>{row.rushingTD}</td>
                {/* Receiving */}
                <td className={classes.bodyCell}>{row.receiving}</td>
                <td className={classes.bodyCell}>{row.receivingYds}</td>
                <td className={classes.bodyCell}>{row.receivingTD}</td>
                {/* Defense */}
                <td className={classes.bodyCell}>{row.tackles}</td>
                <td className={classes.bodyCell}>{row.sacks}</td>
                <td className={classes.bodyCell}>{row.interceptions}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </NamedBox>
  );
}

